import React, { useState, useMemo, useEffect, useCallback } from "react";
import styled, { keyframes, css } from "styled-components";
import moment from "moment";
import EmojiEventsOutlinedIcon from "@mui/icons-material/EmojiEventsOutlined";
import CallOutlinedIcon from "@mui/icons-material/CallOutlined";
import HandshakeOutlinedIcon from "@mui/icons-material/HandshakeOutlined";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import { tokens } from "../../../Shared/tokens";
import { useGetHourlyRaceScoresQuery } from "../../../features/raceReportSlice";
import { subscribeToRaceReportUpdates } from "../../../services/socket";
import { EmployeeRaceDrawer } from "./EmployeeRaceDrawer";

const COLLAPSED_ROWS = 7;

export const RaceBoard = () => {
  const [selected, setSelected] = useState(null);
  const [expanded, setExpanded] = useState(false);

  const today = moment().format("YYYY-MM-DD");

  const { data, isLoading, isError, refetch } = useGetHourlyRaceScoresQuery();

  // Same feed the hourly popup listens to. The popup shows one snapshot and
  // dismisses itself; this board keeps that snapshot on screen and refreshes
  // in place on each hourly recalculation.
  useEffect(() => subscribeToRaceReportUpdates(() => refetch()), [refetch]);

  const rows = useMemo(() => data?.employees ?? [], [data]);

  const stats = useMemo(() => {
    if (!rows.length) return null;
    const total = rows.reduce((sum, e) => sum + e.score, 0);
    return {
      average: Math.round(total / rows.length),
      atTarget: rows.filter((e) => e.score === 100).length,
      noMeeting: rows.filter((e) => e.meetingsCompleted === 0).length,
      idle: rows.filter((e) => e.connectedCalls === 0 && e.meetingsCompleted === 0)
        .length,
      topCalls: rows.reduce(
        (best, e) => (e.connectedCalls > (best?.connectedCalls ?? -1) ? e : best),
        null
      ),
      topMeetings: rows.reduce(
        (best, e) =>
          e.meetingsCompleted > (best?.meetingsCompleted ?? -1) ? e : best,
        null
      ),
    };
  }, [rows]);

  // With everyone on zero the dense ranking puts the whole team at position 1,
  // so a podium would hand medals to whoever sorts first alphabetically. Until
  // somebody actually scores there is no leader to show.
  const hasScores = rows.some((e) => e.score > 0);

  const podium = hasScores ? rows.slice(0, 3) : [];
  const ranked = hasScores ? rows.slice(3) : rows;
  const rest = expanded ? ranked : ranked.slice(0, COLLAPSED_ROWS);

  const handleOpen = useCallback((employee) => setSelected(employee), []);

  return (
    <Card>
      <Header>
        <Heading>
          <TitleRow>
            <EmojiEventsOutlinedIcon fontSize="small" aria-hidden="true" />
            <h2>Daily race</h2>
            <LiveTag title="Refreshes automatically each hour">
              <Dot aria-hidden="true" />
              Live
            </LiveTag>
          </TitleRow>
          <p>
            30 connected calls (3 pts each) plus 1 completed meeting (10 pts)
            makes 100.
          </p>
        </Heading>

        {data?.timestamp && (
          <UpdatedAt>
            <dt>Last update</dt>
            <dd>{moment(data.timestamp).format("h:mm A")}</dd>
          </UpdatedAt>
        )}
      </Header>

      {isLoading && <Message>Loading today's race…</Message>}

      {isError && !isLoading && (
        <Message $tone="danger">
          Could not load the race board. It may be restricted to CEO, admin and
          v3 accounts.
        </Message>
      )}

      {!isLoading && !isError && !rows.length && (
        <Message>
          <GroupsOutlinedIcon />
          No employees are being scored today.
        </Message>
      )}

      {!isLoading && !isError && rows.length > 0 && (
        <>
          {stats && (
            <StatStrip>
              <Stat>
                <dt>Team average</dt>
                <dd>{stats.average}</dd>
              </Stat>
              <Stat>
                <dt>At 100</dt>
                <dd>
                  {stats.atTarget}
                  <small> of {rows.length}</small>
                </dd>
              </Stat>
              <Stat>
                <dt>No meeting yet</dt>
                <dd>{stats.noMeeting}</dd>
              </Stat>
              <Stat>
                <dt>No activity</dt>
                <dd>{stats.idle}</dd>
              </Stat>
            </StatStrip>
          )}

          {/* Research on sales leaderboards is consistent that a single ranked
              number disengages everyone outside the top few. Calling out the
              best on each input gives more than one way to be recognised. */}
          {stats?.topCalls?.connectedCalls > 0 && (
            <Callouts>
              <Callout>
                <CallOutlinedIcon fontSize="small" aria-hidden="true" />
                <span>
                  Most calls: <strong>{stats.topCalls.employeeName}</strong> (
                  {stats.topCalls.connectedCalls})
                </span>
              </Callout>
              {stats.topMeetings?.meetingsCompleted > 0 && (
                <Callout>
                  <HandshakeOutlinedIcon fontSize="small" aria-hidden="true" />
                  <span>
                    Most meetings:{" "}
                    <strong>{stats.topMeetings.employeeName}</strong> (
                    {stats.topMeetings.meetingsCompleted})
                  </span>
                </Callout>
              )}
            </Callouts>
          )}

          {!hasScores && (
            <Notice>
              <strong>No scores recorded yet</strong>
              <span>
                Nobody has a connected call or completed meeting logged today.
                Rankings appear once the first activity lands.
              </span>
            </Notice>
          )}

          <Podium>
            {podium.map((employee, index) => (
              <PodiumCard
                key={employee.employeeId}
                type="button"
                $place={employee.position}
                $index={index}
                onClick={() => handleOpen(employee)}
                aria-label={`${employee.employeeName}, position ${employee.position}, score ${employee.score}`}
              >
                <Place $place={employee.position}>{employee.position}</Place>
                <PodiumName>{employee.employeeName}</PodiumName>
                <PodiumScore>{employee.score}</PodiumScore>
                <PodiumMeta>
                  {employee.connectedCalls} calls · {employee.meetingsCompleted}{" "}
                  mtg
                </PodiumMeta>
              </PodiumCard>
            ))}
          </Podium>

          {rest.length > 0 && (
            <List>
              {rest.map((employee, index) => (
                <Row
                  key={employee.employeeId}
                  type="button"
                  $index={index}
                  onClick={() => handleOpen(employee)}
                >
                  {/* Everyone ties at position 1 while all scores are 0, so a
                      rank number would be noise rather than information. */}
                  <Rank>{hasScores ? employee.position : "–"}</Rank>
                  <NameCell>
                    <strong>{employee.employeeName}</strong>
                    <Gap>{describeGap(employee)}</Gap>
                  </NameCell>
                  <Track
                    role="img"
                    aria-label={`Score ${employee.score} of 100`}
                  >
                    <Fill style={{ width: `${employee.score}%` }} />
                  </Track>
                  <Score>{employee.score}</Score>
                  <Metrics>
                    <span title="Connected calls">
                      <CallOutlinedIcon fontSize="inherit" aria-hidden="true" />
                      {employee.connectedCalls}
                    </span>
                    <span title="Meetings completed">
                      <HandshakeOutlinedIcon
                        fontSize="inherit"
                        aria-hidden="true"
                      />
                      {employee.meetingsCompleted}
                    </span>
                  </Metrics>
                  <ChevronRightIcon fontSize="small" aria-hidden="true" />
                </Row>
              ))}
            </List>
          )}

          {ranked.length > COLLAPSED_ROWS && (
            <ExpandButton
              type="button"
              onClick={() => setExpanded((v) => !v)}
              aria-expanded={expanded}
            >
              {expanded
                ? "Show fewer"
                : `Show all ${rows.length} (${ranked.length - COLLAPSED_ROWS} more)`}
            </ExpandButton>
          )}
        </>
      )}

      <EmployeeRaceDrawer
        employee={selected}
        date={today}
        onClose={() => setSelected(null)}
      />
    </Card>
  );
};

/**
 * What this person still needs, rather than a verdict on them.
 */
function describeGap(employee) {
  const { score, connectedCalls, meetingsCompleted } = employee;
  if (score >= 100) return "Target met";
  if (connectedCalls === 0 && meetingsCompleted === 0) return "No activity yet";

  const callsShort = Math.max(0, 30 - connectedCalls);
  const needsMeeting = meetingsCompleted === 0;

  if (callsShort && needsMeeting)
    return `${callsShort} more calls and 1 meeting`;
  if (callsShort) return `${callsShort} more calls`;
  if (needsMeeting) return "Needs 1 meeting";
  return "Target met";
}

const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.35; }
`;

const riseIn = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to   { opacity: 1; transform: translateY(0); }
`;

/* Standard motion tier: a short staggered entry so rank changes read as
   movement rather than a flicker. */
const stagger = (index) => css`
  animation: ${riseIn} 320ms ${tokens.motion.ease} both;
  animation-delay: ${Math.min(index, 8) * 40}ms;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const Card = styled.section`
  width: 100%;
  background-color: ${tokens.color.surface};
  border: 1px solid ${tokens.color.border};
  border-radius: ${tokens.radius.lg};
  box-shadow: ${tokens.shadow.sm};
  padding: ${tokens.space.xl};
  display: flex;
  flex-direction: column;
  gap: ${tokens.space.lg};
`;

const Header = styled.header`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${tokens.space.lg};
  flex-wrap: wrap;
`;

const Heading = styled.div`
  min-width: 0;

  p {
    margin: ${tokens.space.xs} 0 0;
    font-size: ${tokens.font.md};
    line-height: 1.5;
    color: ${tokens.color.textMuted};
  }
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${tokens.space.sm};

  h2 {
    margin: 0;
    font-size: ${tokens.font.xl};
    color: ${tokens.color.textPrimary};
  }

  > svg {
    color: ${tokens.color.gold};
  }
`;

const LiveTag = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${tokens.space.xs};
  padding: 2px ${tokens.space.sm};
  font-size: ${tokens.font.xs};
  font-weight: 600;
  color: ${tokens.color.success};
  background-color: ${tokens.color.successSoft};
  border-radius: ${tokens.radius.pill};
`;

const Dot = styled.span`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background-color: ${tokens.color.success};
  animation: ${pulse} 2s ease-in-out infinite;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const UpdatedAt = styled.dl`
  margin: 0;
  text-align: right;
  flex-shrink: 0;

  dt {
    font-size: ${tokens.font.sm};
    color: ${tokens.color.textMuted};
  }

  dd {
    margin: ${tokens.space.xs} 0 0;
    font-size: ${tokens.font.lg};
    font-weight: 600;
    color: ${tokens.color.textPrimary};
    font-variant-numeric: tabular-nums;
  }
`;

const Message = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${tokens.space.sm};
  padding: ${tokens.space["2xl"]};
  font-size: ${tokens.font.md};
  text-align: center;
  color: ${(p) =>
    p.$tone === "danger" ? tokens.color.danger : tokens.color.textMuted};

  svg {
    color: ${tokens.color.borderStrong};
  }
`;

const StatStrip = styled.dl`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: ${tokens.space.md};
  margin: 0;
`;

const Stat = styled.div`
  padding: ${tokens.space.md};
  background-color: ${tokens.color.surfaceSubtle};
  border: 1px solid ${tokens.color.border};
  border-radius: ${tokens.radius.md};

  dt {
    font-size: ${tokens.font.sm};
    color: ${tokens.color.textMuted};
  }

  dd {
    margin: ${tokens.space.xs} 0 0;
    font-size: 22px;
    font-weight: 600;
    color: ${tokens.color.textPrimary};

    small {
      font-size: ${tokens.font.sm};
      font-weight: 400;
      color: ${tokens.color.textMuted};
    }
  }
`;

const Notice = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${tokens.space.xs};
  padding: ${tokens.space.lg};
  background-color: ${tokens.color.infoSoft};
  border: 1px solid ${tokens.color.border};
  border-radius: ${tokens.radius.md};

  strong {
    font-size: ${tokens.font.md};
    color: ${tokens.color.textPrimary};
  }

  span {
    font-size: ${tokens.font.md};
    line-height: 1.5;
    color: ${tokens.color.textSecondary};
  }
`;

const ExpandButton = styled.button`
  align-self: center;
  min-height: 36px;
  padding: 0 ${tokens.space.lg};
  font-size: ${tokens.font.sm};
  font-weight: 600;
  color: ${tokens.color.primary};
  background: none;
  border: 1px solid ${tokens.color.border};
  border-radius: ${tokens.radius.md};
  cursor: pointer;
  outline: none;
  transition: background-color ${tokens.motion.base} ${tokens.motion.ease};

  :hover {
    background-color: ${tokens.color.primarySoft};
  }

  :focus-visible {
    box-shadow: ${tokens.shadow.focus};
  }
`;

const Callouts = styled.div`
  display: flex;
  gap: ${tokens.space.md};
  flex-wrap: wrap;
`;

const Callout = styled.div`
  display: inline-flex;
  align-items: center;
  gap: ${tokens.space.sm};
  padding: ${tokens.space.sm} ${tokens.space.md};
  font-size: ${tokens.font.sm};
  color: ${tokens.color.textSecondary};
  background-color: ${tokens.color.primarySoft};
  border-radius: ${tokens.radius.md};

  svg {
    color: ${tokens.color.primary};
  }

  strong {
    color: ${tokens.color.textPrimary};
  }
`;

const Podium = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: ${tokens.space.md};

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const placeColor = (place) =>
  place === 1
    ? { fg: tokens.color.gold, bg: tokens.color.goldSoft }
    : place === 2
    ? { fg: tokens.color.silver, bg: tokens.color.silverSoft }
    : { fg: tokens.color.bronze, bg: tokens.color.bronzeSoft };

const PodiumCard = styled.button`
  ${(p) => stagger(p.$index)}
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${tokens.space.xs};
  padding: ${tokens.space.lg};
  text-align: left;
  cursor: pointer;
  background-color: ${(p) => placeColor(p.$place).bg};
  border: 1px solid ${(p) => placeColor(p.$place).fg}33;
  border-radius: ${tokens.radius.md};
  outline: none;
  transition: transform ${tokens.motion.base} ${tokens.motion.ease},
    box-shadow ${tokens.motion.base} ${tokens.motion.ease};

  :hover {
    transform: translateY(-2px);
    box-shadow: ${tokens.shadow.md};
  }

  :focus-visible {
    box-shadow: ${tokens.shadow.focus};
  }
`;

const Place = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  font-size: ${tokens.font.sm};
  font-weight: 700;
  color: ${tokens.color.onPrimary};
  background-color: ${(p) => placeColor(p.$place).fg};
  border-radius: 50%;
`;

const PodiumName = styled.strong`
  font-size: ${tokens.font.md};
  color: ${tokens.color.textPrimary};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 100%;
`;

const PodiumScore = styled.span`
  font-size: 28px;
  font-weight: 700;
  line-height: 1.1;
  color: ${tokens.color.textPrimary};
`;

const PodiumMeta = styled.span`
  font-size: ${tokens.font.sm};
  color: ${tokens.color.textSecondary};
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
`;

const Row = styled.button`
  ${(p) => stagger(p.$index + 3)}
  display: grid;
  grid-template-columns: 32px minmax(140px, 1.6fr) minmax(80px, 2fr) 40px auto 20px;
  align-items: center;
  gap: ${tokens.space.md};
  width: 100%;
  padding: ${tokens.space.md} ${tokens.space.sm};
  text-align: left;
  cursor: pointer;
  background: none;
  border: none;
  border-bottom: 1px solid ${tokens.color.border};
  outline: none;
  transition: background-color ${tokens.motion.base} ${tokens.motion.ease};

  :hover {
    background-color: ${tokens.color.surfaceSubtle};
  }

  :focus-visible {
    box-shadow: ${tokens.shadow.focus};
  }

  > svg {
    color: ${tokens.color.textMuted};
  }

  @media (max-width: 720px) {
    grid-template-columns: 28px 1fr auto 20px;

    /* The bar and the icon metrics are the first things to go on narrow
       screens; rank, name, gap and score still tell the story. */
    ${"" /* keep specificity simple */}
  }
`;

const Rank = styled.span`
  font-size: ${tokens.font.md};
  font-weight: 600;
  color: ${tokens.color.textMuted};
  text-align: center;
`;

const NameCell = styled.span`
  display: flex;
  flex-direction: column;
  min-width: 0;

  strong {
    font-size: ${tokens.font.md};
    font-weight: 500;
    color: ${tokens.color.textPrimary};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const Gap = styled.span`
  font-size: ${tokens.font.xs};
  color: ${tokens.color.textMuted};
`;

const Track = styled.span`
  height: 6px;
  background-color: ${tokens.color.surfaceHover};
  border-radius: ${tokens.radius.pill};
  overflow: hidden;

  @media (max-width: 720px) {
    display: none;
  }
`;

const Fill = styled.span`
  display: block;
  height: 100%;
  background-color: ${tokens.color.primary};
  border-radius: inherit;
  transition: width 400ms ${tokens.motion.ease};

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const Score = styled.span`
  font-size: ${tokens.font.md};
  font-weight: 600;
  color: ${tokens.color.textPrimary};
  text-align: right;
`;

const Metrics = styled.span`
  display: inline-flex;
  gap: ${tokens.space.md};
  font-size: ${tokens.font.sm};
  color: ${tokens.color.textSecondary};

  span {
    display: inline-flex;
    align-items: center;
    gap: 4px;
  }

  @media (max-width: 720px) {
    display: none;
  }
`;
